import {AfterViewInit, ChangeDetectorRef, Component, ElementRef, HostListener, Injector, NgZone, OnChanges, OnDestroy, OnInit, SimpleChanges, ViewChild, ChangeDetectionStrategy} from "@angular/core";
import { CommonModule } from '@angular/common';
import {
  BackNavigable,
  BackNavigationService,
  Command, CommandStore, showRequestErrors, SOFTLINE_API_FILE, SOFTLINE_FEATURE_COMMANDS,
  SOFTLINE_FEATURE_TITLE,
  TitleStore
} from "@softline/application";
import { base64Decode, base64Encode, Dictionary, isDefined, isDefinedNotEmpty, Store } from '@softline/core';
import { ActivatedRoute, Router } from '@angular/router';
import {animationFrameScheduler, asyncScheduler, BehaviorSubject, filter, from, map, Observable, observeOn, queueScheduler, subscribeOn, Subscription, switchMap, tap} from "rxjs";
import {
  MessageBarStore,
  ModalStore,
  SOFTLINE_FEATURE_MESSAGE_BAR,
  SOFTLINE_FEATURE_MODAL,
  Validators
} from "@softline/ui-core";
import {DynamicModule, FieldOkComponent} from "@softline/dynamic";
import { FormControl, FormGroup, ReactiveFormsModule } from "@angular/forms";
import { SOFTLINE_FEATURE_PRODUKTKALKULATION } from "../produktkalkulation.shared";
import { ProduktkalkulationStore } from "../store";
import {Arbeit, Edelmetall, Legierung, Material, Produktkalkulation} from "../types/produktkalkulation";
import { MaterialDialog } from "../dialogs/material/material.dialog";
import { ArbeitDialog } from "../dialogs/arbeit/arbeit.dialog";
import { EdelmetallkursDialog } from "../dialogs/edelmetallkurs/edelmetallkurs.dialog";
import {FocusMonitor, FocusOrigin} from '@angular/cdk/a11y';

import { CurrencyFormatPipe } from '@softline/ui-core';
import { SecureImagePipe } from '@softline/ui-core';
@Component({
    selector: "soft-produktkalkulation",
    imports: [CommonModule, DynamicModule, ReactiveFormsModule,
    CurrencyFormatPipe,
    SecureImagePipe],
    templateUrl: "./produktkalkulation.page.html",
    changeDetection: ChangeDetectionStrategy.Eager,
    styleUrls: ["./produktkalkulation.page.css"]
})
export class ProduktkalkulationPage implements OnInit, AfterViewInit, OnDestroy, BackNavigable {

  @ViewChild('kunde', { static: true }) private kundeFieldOK?: FieldOkComponent<unknown>
  @ViewChild('arteh', { static: true }) private artehFieldOK?: FieldOkComponent<unknown>
  @ViewChild('artstamm', { static: true }) private artstammFieldOK?: FieldOkComponent<unknown>

  @ViewChild('abfrage', { static: true }) private abfrageButton?: ElementRef<HTMLButtonElement>
  private formSubscription2?: Subscription;

  private formSubscription?: Subscription;

  loading$ = new BehaviorSubject<boolean>(false);

  baseForm = new FormGroup({
    kunde: new FormControl<{id: number} | null>(null, ),
    artstamm: new FormControl<{id: number; cartikelnummer: string } | null>(null, [Validators.required()]),
    arteh: new FormControl<{id: number} | null>(null, [Validators.required()])
  });

  data$: Observable<Produktkalkulation> = this.store.observe(
    SOFTLINE_FEATURE_PRODUKTKALKULATION,
    ProduktkalkulationStore.getters.data
  );

  readonly legierungSum$ = this.data$.pipe(
    filter(o => !!o && Object.keys(o)?.length > 0),
    map(kalkulation => {
      const base = {
        preis: 0,
        gewicht: 0,
        wert: 0
      };

      if (kalkulation?.legierungen && kalkulation?.legierungen?.length > 0) {
        return kalkulation.legierungen.reduce(
          (a, b) => {
            a['preis'] += b.preis ?? 0;
            a['gewicht'] += b.gewicht ?? 0
            a['wert'] += b.wert ?? 0
            return a
          }, base);
      }

      return base
    })
  );

  readonly materialienSum$ = this.data$.pipe(
    filter(o => !!o && Object.keys(o)?.length > 0),
    map(kalkulation => {
      const base = {
        gewicht: 0,
        mengeCt: 0,
        wert: 0
      };

      if (kalkulation?.materialien && kalkulation?.materialien?.length > 0) {
        return kalkulation.materialien.reduce(
          (a, b) => {
            a['mengeCt'] += b.mengeCt ?? 0;
            a['gewicht'] += b.gewicht ?? 0
            a['wert'] += b.wert ?? 0
            return a
          }, base);
      }

      return base
    })
  );

  readonly arbeitSum$ = this.data$.pipe(
    filter(o => !!o && Object.keys(o)?.length > 0),
    map(kalkulation => {
      const base = { wert: 0 };

      if (kalkulation?.arbeit && kalkulation?.arbeit?.length > 0) {
        return kalkulation.arbeit.reduce(
          (a, b) => {
            a['wert'] += b.wert ?? 0
            return a
          }, base
        );
      }

      return base
    })
  );

  readonly image$ = this.data$.pipe(
    map(o => o?.media?.length > 0
      ? o?.media.map(p => SOFTLINE_API_FILE.replace('{{key}}', '') + base64Encode(JSON.stringify(p)))[0]
      : undefined),
  );

  constructor(
    private store: Store,
    protected router: Router,
    protected activatedRoute: ActivatedRoute,
    private injector: Injector,
    private backNavigationService: BackNavigationService,
  ) {}

  async ngOnInit(): Promise<void> {
    this.store.commit(SOFTLINE_FEATURE_TITLE, TitleStore.mutations.setTitle, '#PRODUKTKALKULATION.TITLE');
    this.store.commit(SOFTLINE_FEATURE_COMMANDS, CommandStore.mutations.addSet, {
      name: ProduktkalkulationPage,
      commands: this.createCommands()
    });

    this.formSubscription = this.baseForm.controls.artstamm.valueChanges
      .pipe(
        tap(o => o ?? this.resetPage()),
        filter((o) => !!o),
        filter((artikel): artikel is { id: number, cartikelnummer: string } => !!artikel),
        switchMap(({ id }) => from(this.patchTemplate({ idartstamm: id })))
      )
      .subscribe();


    this.backNavigationService.set(this);
  }

  async ngAfterViewInit(): Promise<void> {
    this.formSubscription2 = this.baseForm.valueChanges
      .pipe(observeOn(asyncScheduler))
      .subscribe(() => {
        if (this.baseForm.valid) {
          this.abfrageButton?.nativeElement.focus();
        }
      });

    const query = this.activatedRoute.snapshot.queryParamMap.get('artikelnummer');

    if (!query) {
      await this.patchTemplate({});
      return;
    }

    if (isDefinedNotEmpty(query)) {
      const result = await this.getArtstamm(query)

      this.baseForm.patchValue(
        { artstamm: result },
        { emitEvent: false, onlySelf: true }
      );

      await this.patchTemplate({ idartstamm: result.id });
      await this.onBaseFormSubmit();
    } else {
      await this.patchTemplate({});
    }
  }

  ngOnDestroy(): void {
    if (this.formSubscription && !this.formSubscription.closed)
      this.formSubscription.unsubscribe();

    if (this.formSubscription2 && !this.formSubscription2.closed)
      this.formSubscription2.unsubscribe();

    this.backNavigationService.set(undefined);
    this.store.commit(SOFTLINE_FEATURE_TITLE, TitleStore.mutations.setTitle, '');
    this.store.commit(SOFTLINE_FEATURE_COMMANDS, CommandStore.mutations.removeSet, ProduktkalkulationPage);
    this.store.commit(SOFTLINE_FEATURE_PRODUKTKALKULATION, ProduktkalkulationStore.mutations.set, undefined);
  }

  async navigateBack(): Promise<void> {
    await this.router.navigate(['/']);
  }

  createCommands(): Command[] {
    return [{
        icon: 'fa-regular fa-calculator',
        name: '#PRODUKTKALKULATION.TITLE',
        class: 'menu main-menu main-menu-top',
        routerLink: ['/produktkalkulation']
      }, {
      icon: 'fa-regular fa-arrows-rotate',
      name: 'Neu Berechnen',
      class: 'menu action-menu action-menu-top',
      canExecute: this.store.observe(SOFTLINE_FEATURE_PRODUKTKALKULATION, ProduktkalkulationStore.getters.data)
        .pipe(map(o => isDefined(o))),
      execute: () => this.recalculate()
    }, {
      icon: 'fa-regular fa-floppy-disk',
      name: 'Angebot speichern',
      class: 'menu action-menu action-menu-top',
      canExecute: this.store.observe(SOFTLINE_FEATURE_PRODUKTKALKULATION, ProduktkalkulationStore.getters.data)
        .pipe(map(o => isDefined(o))),
      execute: () => this.save()
    },
      {
        icon: 'fa-regular fa-sd-card',
        name: 'Stückliste speichern',
        class: 'menu action-menu action-menu-top',
        canExecute: this.store.observe(SOFTLINE_FEATURE_PRODUKTKALKULATION, ProduktkalkulationStore.getters.data)
          .pipe(map(o => isDefined(o))),
        execute: () => this.saveStueckliste()
      }
    ];
  }

  async onBaseFormSubmit(): Promise<void> {
    try {
      const formValue = this.baseForm.value;
      if(!isDefined(formValue) || this.baseForm.invalid)
        return;
      this.loading$.next(true);

      await this.store.dispatch(SOFTLINE_FEATURE_PRODUKTKALKULATION, ProduktkalkulationStore.actions.load, {
        queryParams: {
          idkunde: formValue.kunde?.id,
          idartstamm: formValue.artstamm?.id,
          idarteh: formValue.arteh?.id }
      });

      this.router.navigate(['/produktkalkulation'], {
        queryParams: {
          artikelnummer: formValue.artstamm?.cartikelnummer } });
    }
    catch (e) {
      showRequestErrors(this.store, e);
    }
    finally {
      this.loading$.next(false);
    }
  }

  async editEdelmetall(edelmetall: Edelmetall): Promise<void> {
    const result = await this.store.dispatch(SOFTLINE_FEATURE_MODAL, ModalStore.actions.open<Partial<Edelmetall>, any>(), {
      component: EdelmetallkursDialog,
      data:{
        value: edelmetall
      },
      dismiss: true
    })
    if(result === 'DISMISSED')
      return;
    this.store.commit(SOFTLINE_FEATURE_PRODUKTKALKULATION, ProduktkalkulationStore.mutations.editEdelmetall, result);
    await this.recalculate();
  }

  async addMaterial(): Promise<void> {
    const result = await this.store.dispatch(SOFTLINE_FEATURE_MODAL, ModalStore.actions.open<Partial<Material>, any>(), {
      component: MaterialDialog,
      dismiss: true,
      getInjector: () => this.injector
    })
    if(result === 'DISMISSED')
      return;
    this.store.commit(SOFTLINE_FEATURE_PRODUKTKALKULATION, ProduktkalkulationStore.mutations.addMaterial, result);
    await this.recalculate();
  }

  async editMaterial(material: Material): Promise<void> {
    const result = await this.store.dispatch(SOFTLINE_FEATURE_MODAL, ModalStore.actions.open<Partial<Material>, any>(), {
      component: MaterialDialog,
      data:{
        value: material
      },
      getInjector: () => this.injector,
      dismiss: true
    });

    if (result === 'DISMISSED')
      return;

    this.store.commit(
      SOFTLINE_FEATURE_PRODUKTKALKULATION,
      ProduktkalkulationStore.mutations.editMaterial,
      { old: material, new: result }
    );

    await this.recalculate();
  }

  async deleteMaterial(material: Material): Promise<void> {
    const result = await this.store.dispatch(
      SOFTLINE_FEATURE_MODAL,
      ModalStore.actions.ask,
      'Möchten sie diesen Artikel wirklich löschen?'
    );

    if(result === 'DISMISSED' || result !== 'YES')
      return;

    this.store.commit(SOFTLINE_FEATURE_PRODUKTKALKULATION, ProduktkalkulationStore.mutations.deleteMaterial, material);
    await this.recalculate();
  }

  async addArbeit(): Promise<void> {
    const result = await this.store.dispatch(SOFTLINE_FEATURE_MODAL, ModalStore.actions.open<Partial<Arbeit>, any>(), {
      component: ArbeitDialog,
      dismiss: true
    });

    if (result === 'DISMISSED')
      return;

    this.store.commit(SOFTLINE_FEATURE_PRODUKTKALKULATION, ProduktkalkulationStore.mutations.addArbeit, result);
    await this.recalculate();
  }

  async editArbeit(arbeit: Arbeit): Promise<void> {
    const result = await this.store.dispatch(SOFTLINE_FEATURE_MODAL, ModalStore.actions.open<Partial<Arbeit>, any>(), {
      component: ArbeitDialog,
      data:{
        value: arbeit
      },
      dismiss: true
    })

    if(result === 'DISMISSED')
      return;

    this.store.commit(SOFTLINE_FEATURE_PRODUKTKALKULATION, ProduktkalkulationStore.mutations.editArbeit, { old: arbeit, new: result });
    await this.recalculate();
  }

  async deleteArbeit(arbeit: Arbeit): Promise<void> {
    const result = await this.store.dispatch(SOFTLINE_FEATURE_MODAL, ModalStore.actions.ask,
      'Möchten sie diesen Artikel wirklich löschen?')

    if(result === 'DISMISSED' || result !== 'YES')
      return;

    this.store.commit(SOFTLINE_FEATURE_PRODUKTKALKULATION, ProduktkalkulationStore.mutations.deleteArbeit, arbeit);
    await this.recalculate();
  }

  async save(): Promise<void> {
    try{
      await this.store.dispatch(SOFTLINE_FEATURE_PRODUKTKALKULATION, ProduktkalkulationStore.actions.save);
      await this.store.dispatch(SOFTLINE_FEATURE_MESSAGE_BAR, MessageBarStore.actions.success, 'Das Angebot wurde gespeichert')
    } catch (e) {
      showRequestErrors(this.store, e)
    }
  }

  async saveStueckliste(): Promise<void> {
    try {
      await this.store.dispatch(SOFTLINE_FEATURE_PRODUKTKALKULATION, ProduktkalkulationStore.actions.saveStueckliste);
      await this.store.dispatch(SOFTLINE_FEATURE_MESSAGE_BAR, MessageBarStore.actions.success, 'Die Stückliste wurde gespeichert')
    } catch (e) {
      showRequestErrors(this.store, e)
    }
  }

  private async recalculate(): Promise<void> {
    try {
      await this.store.dispatch(SOFTLINE_FEATURE_PRODUKTKALKULATION, ProduktkalkulationStore.actions.recalculate);
      await this.store.dispatch(SOFTLINE_FEATURE_MESSAGE_BAR, MessageBarStore.actions.success, 'Der Artikel wurde neu berechnet')
    }
    catch (e) {
      showRequestErrors(this.store, e)
    }
  }

  private async patchTemplate(data: Dictionary<unknown> = {}): Promise<void> {
    const { result } = await this.store.dispatch(
      SOFTLINE_FEATURE_PRODUKTKALKULATION,
      ProduktkalkulationStore.actions.default.load,
      { data }
    );

    if (!result)
      return;

    const promises: Promise<any>[] = [];

    if (this.kundeFieldOK && result?.idkunde) {
      promises.push(
        this.kundeFieldOK.strategy.validate('' + result.idkunde).then((fokResult) => {
          if (fokResult && fokResult !== 'CANCELED')
            this.baseForm.patchValue({ kunde: fokResult as any })
        })
      );
    }

    if (this.artehFieldOK && result?.artehBean) {
      promises.push(
        this.artehFieldOK.strategy.validate(result.artehBean?.arteh).then(() => {
          this.baseForm.patchValue({ arteh: result?.artehBean })
        })
      );
    }

    if (promises.length < 1)
      return;

    try {
      await Promise.all(promises);
      this.baseForm.updateValueAndValidity({ onlySelf: true, emitEvent: false });
    } catch (e) {
      console.error(e);
    }
  }

  private async getArtstamm(cartikelnummer: string): Promise<any | null> {
    try {
      const result = await this.artstammFieldOK?.strategy.validate(cartikelnummer);
      return result;
    } catch (e) {
      console.error(e);
      return null
    }
  }

  private async resetPage(): Promise<void> {
    try {
      await this.store.commit(SOFTLINE_FEATURE_PRODUKTKALKULATION, ProduktkalkulationStore.mutations.set, undefined);
      await this.router.navigate(['/produktkalkulation'])
    } catch (e) {
      console.error(e);
    }
  }
}
